import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { PlusCircle, Eye } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import PrimaryCell from "../../../../components/common/PrimaryCell";
import DateRangeFilter, { useDateRangeFilter } from "../../components/list/DateRangeFilter";
import ListExportToolbar from "../../components/list/ListExportToolbar";
import { makeGrandTotalSummary } from "../../components/list/GrandTotalSummary";
import { sumField } from "../../components/list/listExportUtils";
import { useListStockInsQuery } from "../../stockApi";
import { useCanCreateStockIn } from "../../useStockPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const StockInList = () => {
  const canCreate = useCanCreateStockIn();
  const navigate = useNavigate();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const { dateFrom, setDateFrom, dateTo, setDateTo } = useDateRangeFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading } = useListStockInsQuery({
    branchId: selectedBranchId ?? undefined,
    search: search || undefined,
    includeDeleted,
    // 5000, not 100 (2026-10-09): only the newest 100 ever loaded, so older documents
    // vanished and the client-side date filter could not reach them. Volumes are small
    // (remote: 536 stock-outs, 177 LPOs) - same cap as Stock Balances.
    pageSize: 5000,
  });

  const filteredItems = (data?.items || []).filter((r) => {
    if (!r.stockInDate) return true;
    const d = dayjs(r.stockInDate).format("YYYY-MM-DD");
    return (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
  });

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const exportColumns = [
    { header: "Stock-In No", value: (r) => r.stockInNo },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "LPO No", value: (r) => r.lpoNumber || "Direct" },
    { header: "Supplier", value: (r) => r.supplierName },
    { header: "Date", value: (r) => (r.stockInDate ? new Date(r.stockInDate).toLocaleDateString() : "-") },
    // VAT columns (2026-09-14): Amount is ex VAT (the stored TotalAmount), VAT and
    // Total Inc VAT come from Batch172.
    { header: "Amount (ex VAT)", value: (r) => Number(r.totalAmount || 0).toFixed(2), align: "right" },
    { header: "VAT", value: (r) => Number(r.vatAmount || 0).toFixed(2), align: "right" },
    { header: "Total Inc VAT", value: (r) => Number(r.grandTotal || 0).toFixed(2), align: "right" },
    { header: "Status", value: (r) => (r.isDeleted ? "Deleted" : "Posted") },
  ];
  const exportTotals = {
    "Amount (ex VAT)": sumField(filteredItems, "totalAmount").toFixed(2),
    VAT: sumField(filteredItems, "vatAmount").toFixed(2),
    "Total Inc VAT": sumField(filteredItems, "grandTotal").toFixed(2),
  };

  const columns = [
    {
      // Supplier leads, Stock-In number is the link underneath (2026-09-12).
      title: "Stock-In",
      dataIndex: "stockInNo",
      render: (value, record) => (
        <PrimaryCell primary={record.supplierName} secondary={value} to={paths.stockInDetail(record.recordGuid)} />
      ),
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "LPO No", dataIndex: "lpoNumber", render: (v) => v || "Direct" },
    { title: "Date", dataIndex: "stockInDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { title: "Amount (ex VAT)", dataIndex: "totalAmount", render: (v) => Number(v || 0).toFixed(2) },
    { title: "VAT", dataIndex: "vatAmount", render: (v) => Number(v || 0).toFixed(2) },
    { title: "Total Inc VAT", dataIndex: "grandTotal", render: (v) => <strong>{Number(v || 0).toFixed(2)}</strong> },
    {
      title: "Status",
      dataIndex: "isDeleted",
      render: (v) => (v ? <span className="badge badge-linedanger">Deleted</span> : <span className="badge badge-linesuccess">Posted</span>),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <button type="button" className="btn btn-link p-2" title="View" onClick={() => navigate(paths.stockInDetail(record.recordGuid))}>
          <Eye size={16} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Stock-In (GRN)</h4>
              <h6>Goods received against an LPO, or direct/ad-hoc purchase</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn">
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.stockInNew)}>
                <PlusCircle className="me-2" size={16} />
                New Stock-In
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search Stock-In No or Supplier"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <DateRangeFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="stockin-include-deleted"
                    checked={includeDeleted}
                    onChange={(e) => setIncludeDeleted(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="stockin-include-deleted">Show deleted</label>
                </div>
                <ListExportToolbar
                  title="Stock-In (GRN)"
                  subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={filteredItems}
                  totals={exportTotals}
                  fileName="StockIn"
                  groupBy={(r) => r.supplierName || "Unknown Supplier"}
                />
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={filteredItems}
                rowKey="recordGuid"
                loading={isLoading}
                summary={makeGrandTotalSummary(columns, filteredItems, ["totalAmount", "vatAmount", "grandTotal"], "stockInNo")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockInList;
